import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  LayoutDashboard, 
  Users, 
  UserCheck, 
  UserCog, 
  Settings,
  Menu,
  X,
  BarChart3,
  Shield,
  LogOut,
  Bell,
  Search,
  ChevronDown,
  ChevronRight,
  ShieldCheck,
  UserPlus,
  Lock,
  User,
  ShoppingCart
} from 'lucide-react';

const AdminSidebar = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState(['/admin/users', '/admin/roles']);

  const menuItems = [
    {
      title: 'Dashboard',
      icon: LayoutDashboard,
      path: '/admin',
      exact: true,
      description: 'Tổng quan hệ thống'
    },
    {
      title: 'Quản lý đơn hàng',
      icon: ShoppingCart,
      path: '/admin/orders',
      description: 'Quản lý tất cả đơn hàng'
    },
    {
      title: 'Quản lý User',
      icon: Users,
      path: '/admin/users',
      submenu: [
        {
          title: 'CRUD User',
          icon: UserCog,
          path: '/admin/users/crud',
          description: 'Quản lý tất cả users'
        },
        {
          title: 'Phê duyệt',
          icon: UserCheck,
          path: '/admin/users/approval',
          description: 'Phê duyệt yêu cầu'
        }
      ]
    },
    {
      title: 'Quản lý Role',
      icon: ShieldCheck,
      path: '/admin/roles',
      submenu: [
        {
          title: 'Danh sách Role',
          icon: Shield,
          path: '/admin/roles/list',
          description: 'Xem tất cả roles'
        },
        {
          title: 'Thêm Role',
          icon: UserPlus,
          path: '/admin/roles/add',
          description: 'Tạo role mới'
        }
      ]
    },
    {
      title: 'Thông tin cá nhân',
      icon: User,
      path: '/admin/profile',
      description: 'Cài đặt tài khoản'
    }
  ];

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  const isSubmenuActive = (submenuPath) => {
    return location.pathname === submenuPath;
  };

  const toggleSubmenu = (path) => {
    setExpandedMenus(prev => 
      prev.includes(path) 
        ? prev.filter(item => item !== path)
        : [...prev, path]
    );
  };

  const isSubmenuExpanded = (path) => {
    return expandedMenus.includes(path);
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <>
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 bg-white rounded-lg shadow-lg hover:bg-gray-50 transition-colors"
        >
          {isMobileMenuOpen ? (
            <X className="w-6 h-6 text-gray-600" />
          ) : (
            <Menu className="w-6 h-6 text-gray-600" />
          )}
        </button>
      </div>

      {/* Mobile overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed top-0 left-0 h-full bg-white shadow-lg z-40 transition-transform duration-300 ease-in-out
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        w-64 lg:w-64
      `}>
        <div className="p-4 lg:p-6 h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl lg:text-2xl font-bold text-gray-800">Admin</h1>
            </div>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="lg:hidden p-1 hover:bg-gray-100 rounded"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Tìm kiếm..."
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 space-y-2">
            {menuItems.map((item) => (
              <div key={item.path}>
                {item.submenu ? (
                  // Menu item with submenu
                  <div>
                    <div className="flex items-center justify-between">
                      <Link
                        to={item.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`flex-1 flex items-center px-3 lg:px-4 py-3 text-sm font-medium rounded-lg transition-colors group ${
                          isActive(item.path)
                            ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-600'
                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                      >
                        <item.icon className={`w-4 h-4 lg:w-5 lg:h-5 mr-3 ${
                          isActive(item.path) ? 'text-blue-600' : 'text-gray-500 group-hover:text-gray-700'
                        }`} />
                        <span className="text-sm lg:text-base">{item.title}</span>
                      </Link>
                      <button
                        onClick={() => toggleSubmenu(item.path)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        {isSubmenuExpanded(item.path) ? (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-500" />
                        )}
                      </button>
                    </div>
                    
                    {/* Submenu */}
                    {isSubmenuExpanded(item.path) && (
                      <div className="ml-4 lg:ml-8 mt-2 space-y-1">
                        {item.submenu.map((subItem) => (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={`flex items-center px-3 lg:px-4 py-2 text-sm rounded-lg transition-colors group ${
                              isSubmenuActive(subItem.path)
                                ? 'bg-blue-50 text-blue-600 border-l-2 border-blue-400'
                                : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                            }`}
                            title={subItem.description}
                          >
                            <subItem.icon className={`w-3 h-3 lg:w-4 lg:h-4 mr-2 ${
                              isSubmenuActive(subItem.path) ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-600'
                            }`} />
                            <span className="text-xs lg:text-sm">{subItem.title}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  // Regular menu item without submenu
                  <Link
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center px-3 lg:px-4 py-3 text-sm font-medium rounded-lg transition-colors group ${
                      isActive(item.path)
                        ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                    title={item.description}
                  >
                    <item.icon className={`w-4 h-4 lg:w-5 lg:h-5 mr-3 ${
                      isActive(item.path) ? 'text-blue-600' : 'text-gray-500 group-hover:text-gray-700'
                    }`} />
                    <span className="text-sm lg:text-base">{item.title}</span>
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Footer */}
          <div className="mt-auto pt-6 border-t border-gray-200">
            {/* Notifications */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Bell className="w-4 h-4 text-gray-500" />
                <span className="text-xs text-gray-500">Thông báo</span>
              </div>
              <span className="w-2 h-2 bg-red-500 rounded-full"></span>
            </div>

            {/* User Info */}
            <div className="flex items-center space-x-3 mb-4">
              {user?.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.name}
                  className="w-8 h-8 rounded-full"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                  <span className="text-sm font-medium text-white">
                    {user?.name?.charAt(0).toUpperCase() || 'A'}
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.name || 'Admin'}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {user?.email || 'admin@example.com'}
                </p>
              </div>
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-3 py-2 text-sm text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4 mr-3" />
              Đăng xuất
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminSidebar; 